import { IconButton, Typography } from '@material-ui/core';
import { Column, Columns, Inline } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { formatCurrency, formatPlural } from 'shared/helpers/IntlHelpers';
import { trackEvent } from 'shared/helpers/Analytics';
import { InstantTransferBannerGradientBox } from '../InstantTransferBannerGradientBox';
import { Close } from 'shared/icons/Close';
import { InstantTransferReminderDialogs } from './InstantTransferReminderDialogs';
import { useInstantTransferNudgingStatistics } from 'shared/modules/superpay/SuperPayAPI';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useNavigate } from 'react-router-dom';
import { parseURITemplate } from '@superdispatch/uri';
import { useInstantTransferBannerAccessTime } from './useInstantTransferBannerAccessTime';
import { usePermissionAccess } from 'shared/modules/permission/data/usePermissionAccess';
import { LockedIcon } from 'shared/icons/LockIcon';
import { UpgradePlanDialog } from 'carrier/loads/core/UpgradePlanDialog';

interface PaymentBannerProps {
  source: string;
  superpayStatus: string;
  amount: number;
  loads_count: number;
}
function InstantTransferBannerContent({
  amount,
  loads_count,
  source,
  superpayStatus,
}: PaymentBannerProps) {
  const [dialog, setDialog] = useState<'reminder' | 'upgrade' | null>(null);
  const fasterValue = formatCurrency(amount, { minimumFractionDigits: 0 });

  const loadsValue = `${loads_count} recent ${formatPlural(loads_count, 'load', 'loads')}`;

  const navigate = useNavigate();
  const { data: carrierSettings } = useCarrierSettings();

  const isInstantTransferAvailable =
    !!carrierSettings?.superpay.instant_transfer_available;

  const shouldShowLockIcon = !isInstantTransferAvailable;

  return (
    <InstantTransferBannerGradientBox aria-label="Instant Transfer Banner">
      <Columns align="center">
        <Column>
          <Inline
            verticalAlign="center"
            space="xsmall"
            horizontalAlign="center"
          >
            <Typography variant="body1">
              Delivered loads, cash today!
            </Typography>
            <Typography>
              {`${fasterValue} from ${loadsValue} is on the way - skip the wait and get paid now.`}
            </Typography>
            <Button
              startIcon={shouldShowLockIcon ? <LockedIcon /> : undefined}
              variant="neutral"
              size="small"
              onClick={() => {
                if (shouldShowLockIcon) {
                  setDialog('upgrade');
                } else {
                  navigate(
                    parseURITemplate(
                      '/loads{?stage,order_by,superpay_status,utm_medium}',
                      {
                        stage: 'delivered',
                        order_by: 'superpay_status',
                        superpay_status: superpayStatus,
                        utm_medium: `${superpayStatus} Banner`,
                      },
                    ),
                  );
                  trackEvent(
                    'Carrier Clicks Get Paid on Instant Transfer Banner',
                    {
                      utm_medium: source,
                      available_for_instant_transfer_count: loads_count,
                      overall_value_of_instant_transfer: amount,
                    },
                  );
                }
              }}
            >
              Get Paid
            </Button>
          </Inline>
        </Column>
        <Column width="content">
          <IconButton
            aria-label="Close Instant Transfer Banner"
            size="small"
            onClick={() => {
              setDialog('reminder');
            }}
          >
            <Close />
          </IconButton>
        </Column>
      </Columns>
      <InstantTransferReminderDialogs
        isOpen={dialog === 'reminder'}
        onClose={() => {
          setDialog(null);
        }}
      />
      <UpgradePlanDialog
        open={dialog === 'upgrade'}
        onClose={() => setDialog(null)}
        type="instant_transfer"
        source="Banner"
      />
    </InstantTransferBannerGradientBox>
  );
}

export function InstantPayBanner({ source }: { source: string }) {
  const { data: carrierSettings } = useCarrierSettings();
  const isInstantTransferNudgingEnabled =
    carrierSettings?.superpay.instant_transfer_nudging_enabled;
  const { data: instantTransfer } = useInstantTransferNudgingStatistics({
    enabled: isInstantTransferNudgingEnabled,
  });
  const shouldHideInstantTransferBanner = usePermissionAccess({
    name: 'instant_transfer',
    permission: 'can_view',
  });

  const { getAccess } = useInstantTransferBannerAccessTime();

  const shouldHideBanner =
    !isInstantTransferNudgingEnabled ||
    !instantTransfer ||
    !getAccess() ||
    shouldHideInstantTransferBanner;

  if (shouldHideBanner) {
    return null;
  }

  return (
    <InstantTransferBannerContent
      amount={instantTransfer.amount}
      loads_count={instantTransfer.loads_count}
      source={source}
      superpayStatus="Available for Instant Transfer"
    />
  );
}
